import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { Subscription } from 'rxjs';
import * as jsPDF from 'jspdf';
import {
  AnalyticsResponse,
  ExcludedGroup,
  ReportRow,
  AnalyticsService,
} from '../Services/analytics.service';

Chart.register(...registerables);
// The export photographs the canvas, so it is drawn at twice the size
// even on a display that would not ask for it.
Chart.defaults.devicePixelRatio = 2;

/** One chart on the page. */
interface ChartSpec {
  key: string;
  canvasId: string;
  type: 'line' | 'bar';
  metricIndexes: number[];
  horizontal?: boolean;
}

/** Marks the line that carries the earlier of the two compared periods. */
const EARLIER_SUFFIX = ' (earlier period)';

/** How many items a comparison chart draws before the reader turns more on. */
const VISIBLE_AT_FIRST = 3;

/** A table this long or longer splits into two columns. */
const SPLIT_ABOVE = 8;

/** The language codes MyLink sends, written out for the page. */
const LANGUAGE_NAMES: { [code: string]: string } = {
  ar: 'Arabic',
  en: 'English',
  es: 'Spanish',
  kmr: 'Kurmanji',
  ku: 'Kurdish',
  prs: 'Persian',
  ps: 'Pashto',
  so: 'Somali',
  uk: 'Ukrainian',
  vi: 'Vietnamese',
};

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.css'],
})
export class AnalyticsComponent implements AfterViewInit, OnDestroy {
  @ViewChildren('chartCanvas') canvases: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('compareCanvas') compareCanvases: QueryList<ElementRef<HTMLCanvasElement>>;

  public readonly ranges: { key: string; label: string }[] = [
    { key: 'all', label: 'All time' },
    { key: '12m', label: 'Last 12 months' },
    { key: '90d', label: 'Last 90 days' },
    { key: '30d', label: 'Last 30 days' },
  ];

  public tab: 'overview' | 'compare' = 'overview';

  /** Everything that can go in the export, in the order it is written. */
  public readonly exportBlocks: {
    id: string;
    tab: 'overview' | 'compare';
    title: string;
  }[] = [
    { id: 'usageOverTime', tab: 'overview', title: 'How many people use MyLink?' },
    { id: 'engagement', tab: 'overview', title: 'How long do people stay?' },
    { id: 'programEngagement', tab: 'overview', title: 'Did people find something to act on?' },
    { id: 'topCategories', tab: 'overview', title: 'Categories people picked' },
    { id: 'cityCategories', tab: 'overview', title: 'What each city is looking for' },
    { id: 'topSearches', tab: 'overview', title: 'What people searched for' },
    { id: 'emptySearches', tab: 'overview', title: 'Searches that found nothing' },
    { id: 'trafficQuality', tab: 'overview', title: 'Where people are' },
    { id: 'languageEngagement', tab: 'overview', title: 'Language' },
    { id: 'visitors', tab: 'compare', title: 'Compare: people' },
    { id: 'categoryShare', tab: 'compare', title: 'Compare: categories people picked' },
    { id: 'searchShare', tab: 'compare', title: 'Compare: what people searched for' },
    { id: 'cityShare', tab: 'compare', title: 'Compare: where people are' },
    { id: 'platformShare', tab: 'compare', title: 'Compare: phone, tablet, or computer' },
    { id: 'languageShare', tab: 'compare', title: 'Compare: language' },
  ];

  public chosen: { [id: string]: boolean } = {};
  public showExport = false;
  public exporting = false;
  public range = 'all';
  public showCustom = false;
  public customStart: Date | null = null;
  public customEnd: Date | null = null;

  public compareLaterStart: Date | null = null;
  public compareLaterEnd: Date | null = null;
  public compareEarlierStart: Date | null = null;
  public compareEarlierEnd: Date | null = null;
  /** Each comparison on the Compare tab, in the order they are drawn. */
  public readonly compareSections: {
    key: string;
    trend: string;
    title: string;
    unit: string;
    /** A caution to print with the card, when the data needs one. */
    note?: string;
  }[] = [
    {
      key: 'categoryShare',
      trend: 'categoryTrend',
      title: 'Categories people picked',
      unit: 'picks',
      note: 'Google Analytics reports a measurement only from the day it ' +
        'was registered. Category picks were registered in mid-2024, so ' +
        'nothing shows before then. The website stopped sending them in ' +
        'September 2025, when the v2 rewrite dropped the events. The fix ' +
        'was released in September 2026, so dates in between count the ' +
        'phone app only. A category picked in another language counts ' +
        'under its translated name, so some categories appear here more ' +
        'than once.',
    },
    {
      key: 'searchShare',
      trend: 'searchTrend',
      title: 'What people searched for',
      unit: 'searches',
      note: 'Google Analytics reports a measurement only from the day it ' +
        'was registered. Search text was registered in mid-2024, so nothing ' +
        'shows before then. The website kept sending searches throughout, ' +
        'so no gap follows.',
    },
    {
      key: 'cityShare',
      trend: 'cityTrend',
      title: 'Where people are',
      unit: 'people',
      note: 'This is where the visitor\'s device is, which Google works out ' +
        'from the network. It is not the MyLNK location they chose.',
    },
    { key: 'platformShare', trend: 'platformTrend', title: 'Phone, tablet, or computer', unit: 'people' },
    {
      key: 'languageShare',
      trend: 'languageTrend',
      title: 'Language',
      unit: 'people',
      note: 'This is the language the visitor\'s phone or browser is set to, ' +
        'not the language they picked in MyLink. Visitors GA could not match ' +
        'to a language are grouped as "(other)".',
    },
  ];

  /** Enough colours for the lines without them blurring together. */
  private readonly lineColours = [
    '#3f51b5', '#e53935', '#43a047', '#fb8c00', '#8e24aa',
    '#00acc1', '#6d4c41', '#c0ca33',
  ];

  public comparePeople: { earlier: number; later: number; change: number | null } =
    { earlier: 0, later: 0, change: null };
  public compareTotals: {
    measure: string;
    explain: string;
    earlier: number;
    later: number;
    /** Null when the earlier period held nothing to measure against. */
    change: number | null;
    /** The two values, already written out, because they carry mixed units. */
    earlierText: string;
    laterText: string;
  }[] = [];

  public shares: {
    [key: string]: { name: string; first: number; second: number; change: number }[];
  } = {};
  /** The items each comparison draws a small chart for. */
  public compareTop: { [key: string]: string[] } = {};
  // Switching tabs destroys and rebuilds every chart, and the export switches
  // tabs to photograph them. Without this the reader's choices would be lost
  // on the way to the PDF.
  /** Items the reader turned off, by comparison then by name. */
  private compareHidden: { [key: string]: { [name: string]: boolean } } = {};
  public hideAutomated = true;

  // Derived once per load. Getters that build a new array are re-run on every
  // change detection pass, and Angular then sees a different array each time.
  public places: ReportRow[] = [];
  /** The category rows, in one or two columns. See splitInTwo. */
  public categoryColumns: ReportRow[][] = [];
  public languages: ReportRow[] = [];
  /** One row per language, carrying both what a device is set to and what
      the visitor picked in MyLink. */
  public languageRows: {
    name: string;
    people: number;
    picked: number;
    seconds: number;
  }[] = [];
  public cityCategories: { city: string; rows: ReportRow[] }[] = [];
  public automatedCount = 0;
  public excludedPlaces: ExcludedGroup[] = [];
  public showFilterDetail = false;
  public totalPeople = 0;
  public totalEngagedVisits = 0;
  public loading = false;
  public errorMessage = '';
  public data: AnalyticsResponse | null = null;

  private charts: Chart[] = [];
  private viewReady = false;
  /** The report request now in flight. A new load cancels it. */
  private pending: Subscription | null = null;

  private readonly specs: ChartSpec[] = [
    { key: 'usageOverTime', canvasId: 'usageOverTime', type: 'line', metricIndexes: [1] },
    { key: 'programEngagement', canvasId: 'programEngagement', type: 'bar', metricIndexes: [0], horizontal: true },
    { key: 'topCategories', canvasId: 'topCategories', type: 'bar', metricIndexes: [0], horizontal: true },
  ];

  constructor(private analyticsService: AnalyticsService) {
    this.exportBlocks.forEach((block) => (this.chosen[block.id] = true));
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.load();
  }

  ngOnDestroy(): void {
    // Cancel first. A response that lands after this would otherwise draw
    // charts on canvases Angular already removed, and nothing destroys those.
    this.pending?.unsubscribe();
    this.pending = null;
    this.viewReady = false;
    this.destroyCharts();
  }

  public setTab(name: 'overview' | 'compare'): void {
    if (this.tab === name) return;
    this.tab = name;
    // The canvases for the other tab only exist once it is shown.
    setTimeout(() => this.drawCharts(), 0);
  }

  public setRange(key: string): void {
    this.showCustom = false;
    if (this.range === key) return;
    this.range = key;
    this.load();
  }

  public openCustom(): void {
    this.showCustom = !this.showCustom;
  }

  /** Applies the typed dates. Both are needed, and in order. */
  public applyCustom(): void {
    if (!this.customStart || !this.customEnd) return;
    if (this.customStart > this.customEnd) return;
    this.range = 'custom';
    this.showCustom = false;
    this.load();
  }

  /** Drops the typed dates and goes back to the whole history. */
  public clearCustom(): void {
    this.customStart = null;
    this.customEnd = null;
    this.showCustom = false;
    if (this.range !== 'custom') return;
    this.range = 'all';
    this.load();
  }

  /** GA wants YYYY-MM-DD, in the viewer's own dates rather than UTC. */
  private asDate(value: Date | null): string | undefined {
    if (!value) return undefined;
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  /** "Ashburn, Virginia", or the country when there is no state. */
  public placeLabel(group: ExcludedGroup): string {
    const area =
      group.region && group.region !== '(not set)' ? group.region : group.country;
    return area ? `${group.city}, ${area}` : group.city;
  }

  public toggleFilterDetail(): void {
    this.showFilterDetail = !this.showFilterDetail;
  }

  public toggleAutomated(): void {
    this.hideAutomated = !this.hideAutomated;
    // The filter runs on the server, so the numbers have to be fetched again.
    this.load();
  }

  public load(): void {
    this.loading = true;
    this.errorMessage = '';
    try {
      this.request();
    } catch (error) {
      this.loading = false;
      this.errorMessage = 'Could not start the request. Sign out and back in.';
    }
  }

  private request(): void {
    const custom = this.range === 'custom';
    // A range button and the Compare button both reach here. Only send the
    // typed periods when they pass the same check the Compare button makes,
    // or a range click would quietly apply periods the page is warning about.
    const periods = this.comparisonReady ?
      {
        aStart: this.asDate(this.compareLaterStart),
        aEnd: this.asDate(this.compareLaterEnd),
        bStart: this.asDate(this.compareEarlierStart),
        bEnd: this.asDate(this.compareEarlierEnd),
      } :
      {};

    // Drop the answer to the last question before asking a new one. Without
    // this the slower of two responses wins, and the cards stop matching the
    // range named above them.
    this.pending?.unsubscribe();
    this.pending = this.analyticsService
      .getReport(
        this.range,
        !this.hideAutomated,
        custom ? this.asDate(this.customStart) : undefined,
        custom ? this.asDate(this.customEnd) : undefined,
        periods
      )
      .subscribe(
      (response) => {
        this.data = response;
        this.rememberComparison(response);
        this.prepare();
        this.loading = false;
        setTimeout(() => this.drawCharts(), 0);
      },
      (error) => {
        this.loading = false;
        this.errorMessage =
          error?.status === 403
            ? 'Your account does not have administrator access to the analytics data.'
            : 'Could not load the analytics data. Try again in a moment.';
      }
    );
  }

  /** Size of a change, without its direction. */
  public abs(value: number | null): number {
    return value === null ? 0 : Math.abs(value);
  }

  public rows(key: string): ReportRow[] {
    return this.data?.reports?.[key]?.rows ?? [];
  }

  /**
   * True when the usage chart groups by month, false when it groups by day.
   * The server decides this, so read its answer rather than repeat the rule.
   */
  public get usageByMonth(): boolean {
    return this.data?.reports?.['usageOverTime']?.dimensionNames?.[0] === 'month';
  }

  /** The range as real dates, rather than a name like "all time". */
  public get rangeDates(): string {
    if (!this.data) return '';
    const show = (value: Date) =>
      value.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });

    const today = new Date();
    const back = (days: number) =>
      new Date(today.getTime() - days * 86400000);

    // One less than the name, because the window includes both ends. The
    // last 30 days runs from 29 days ago to today. These have to match
    // RANGES in GetAnalyticsReport.ts, which the function reports from.
    let from: Date;
    if (this.range === 'custom' && this.customStart) {
      from = this.customStart;
    } else if (this.range === '30d') {
      from = back(29);
    } else if (this.range === '90d') {
      from = back(89);
    } else if (this.range === '12m') {
      from = back(364);
    } else {
      from = new Date('2019-01-01T00:00:00');
    }

    const to =
      this.range === 'custom' && this.customEnd ? this.customEnd : today;
    return `${show(from)} to ${show(to)}`;
  }

  public get rangeLabel(): string {
    if (this.range === 'custom' && this.data) {
      return `${this.data.startDate} to ${this.data.endDate}`;
    }
    const match = this.ranges.find((item) => item.key === this.range);
    return match ? match.label : '';
  }

  /** Works out everything the template reads, once per load or toggle. */
  private prepare(): void {
    this.places = this.rows('trafficQuality');
    this.languages = this.rows('languageEngagement');
    this.languageRows = this.joinLanguages();
    this.excludedPlaces = (this.data && this.data.excludedPlaces) || [];
    this.automatedCount = this.excludedPlaces.length;

    this.cityCategories = this.groupCityCategories();
    this.categoryColumns = this.splitInTwo(this.rows('topCategories'));
    this.buildShares();

    const usage = this.rows('usageOverTime');
    this.totalEngagedVisits = usage.reduce((sum, row) => sum + row.metrics[0], 0);
    this.totalPeople = usage.reduce((sum, row) => sum + row.metrics[1], 0);

  }

  /** The top categories in each place, grouped under that place. */
  private groupCityCategories(): { city: string; rows: ReportRow[] }[] {
    const grouped = new Map<string, ReportRow[]>();

    this.rows('cityCategories').forEach((row) => {
      const city = row.dimensions[0];
      const region = row.dimensions[1];
      if (!city || city === '(not set)') return;
      // Keyed on place and state, so two cities of the same name stay apart.
      const place = region && region !== '(not set)' ? `${city}, ${region}` : city;
      if (!grouped.has(place)) grouped.set(place, []);
      const list = grouped.get(place);
      if (list.length < 5) list.push(row);
    });

    return Array.from(grouped.entries())
      .map(([city, rows]) => ({ city, rows }))
      .sort((a, b) => this.total(b.rows) - this.total(a.rows))
      .slice(0, 6);
  }

  /** Keeps the pickers showing whatever periods the function actually used. */
  private rememberComparison(response: AnalyticsResponse): void {
    const dates = response.comparison;
    if (!dates) return;
    const parse = (value: string) =>
      value ? new Date(`${value}T00:00:00`) : null;
    if (!this.compareLaterStart) this.compareLaterStart = parse(dates.firstStart);
    if (!this.compareLaterEnd) this.compareLaterEnd = parse(dates.firstEnd);
    if (!this.compareEarlierStart) this.compareEarlierStart = parse(dates.secondStart);
    if (!this.compareEarlierEnd) this.compareEarlierEnd = parse(dates.secondEnd);
  }

  /**
   * Why the Compare button is off, or an empty string when it is on.
   *
   * Overlapping periods count the shared days on both sides, which is not a
   * comparison, so they are refused rather than quietly averaged.
   */
  public get comparisonProblem(): string {
    if (
      !this.compareEarlierStart ||
      !this.compareEarlierEnd ||
      !this.compareLaterStart ||
      !this.compareLaterEnd
    ) {
      return 'Pick all four dates.';
    }
    if (this.compareEarlierStart > this.compareEarlierEnd) {
      return 'The earlier period starts after it ends.';
    }
    if (this.compareLaterStart > this.compareLaterEnd) {
      return 'The later period starts after it ends.';
    }
    if (this.compareEarlierEnd >= this.compareLaterStart) {
      return 'The two periods overlap. Pick periods that do not share any days.';
    }
    return '';
  }

  public get comparisonReady(): boolean {
    return this.comparisonProblem === '';
  }

  public applyComparison(): void {
    if (!this.comparisonReady) return;
    this.load();
  }

  /**
   * Works out each row's share of its period, for every comparison.
   *
   * Share rather than counts, because counts mostly follow how many people
   * visited. Share says what those people needed.
   */
  private buildShares(): void {
    const totals = this.data && this.data.reports['visitorsCompare'];
    const later = totals && totals.rows.length ? totals.rows[0].metrics[0] : 0;
    const earlier =
      totals && totals.previousRows.length ? totals.previousRows[0].metrics[0] : 0;
    // Null, not zero, when the earlier period holds nothing. A rise from no
    // visitors to 400 is not "no change", and the page says "new" instead.
    this.comparePeople = {
      earlier,
      later,
      change: earlier ? ((later - earlier) / earlier) * 100 : null,
    };

    const at = (index: number, previous: boolean) => {
      const rows = previous ? (totals ? totals.previousRows : []) : (totals ? totals.rows : []);
      return rows.length ? rows[0].metrics[index] : 0;
    };
    const line = (measure: string, explain: string, index: number, minutes = false) => {
      const before = at(index, true);
      const now = at(index, false);
      const write = (value: number) => minutes ?
        `${(value / 60).toFixed(1)} min` :
        Math.round(value).toLocaleString();
      return {
        measure,
        explain,
        earlier: before,
        later: now,
        change: before ? ((now - before) / before) * 100 : null,
        earlierText: write(before),
        laterText: write(now),
      };
    };

    this.compareTotals = totals && (earlier || later) ?
      [
        line(
          'People',
          'Each person counts once for the whole period, however many times they come back.',
          0
        ),
        line(
          'Visits',
          'Every visit counted. One person visiting twice counts twice.',
          1
        ),
        line(
          'Engaged visits',
          'The visits that lasted over 10 seconds, reached a second page, or did something.',
          2
        ),
        line(
          'Average visit',
          'How long a visit lasted, from the first action to the last. Idle time counts.',
          3,
          true
        ),
      ] :
      [];

    this.shares = {};
    this.compareTop = {};
    this.compareSections.forEach((section) => {
      this.shares[section.key] = this.sharesFor(section.key);
      // The rows are ordered risers first, so taking the first six would take
      // six risers. Pick the six that moved most in either direction.
      this.compareTop[section.key] = this.shares[section.key]
        .slice()
        .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
        .slice(0, 6)
        .map((row) => row.name);
    });
  }

  /**
   * Puts both language measures on one row each.
   *
   * The device language and the language a visitor picked are different
   * things, and reading them side by side is the point of the card.
   *
   * @return {{name: string, people: number, picked: number, seconds: number}[]}
   *   One row for each language either measure saw.
   */
  private joinLanguages() {
    const found = new Map<string, {
      name: string; people: number; picked: number; seconds: number;
    }>();

    this.rows('languageEngagement').forEach((row) => {
      found.set(row.dimensions[0], {
        name: row.dimensions[0],
        people: row.metrics[0],
        picked: 0,
        seconds: row.metrics[1],
      });
    });

    this.rows('chosenLanguage').forEach((row) => {
      const name = LANGUAGE_NAMES[row.dimensions[0]] || row.dimensions[0];
      const already = found.get(name);
      if (already) {
        already.picked = row.metrics[0];
        return;
      }
      found.set(name, { name, people: 0, picked: row.metrics[0], seconds: 0 });
    });

    return Array.from(found.values())
      .sort((a, b) => b.people - a.people || b.picked - a.picked);
  }

  private sharesFor(key: string) {
    const report = this.data && this.data.reports[key];
    if (!report) return [];

    const sum = (rows: ReportRow[]) =>
      rows.reduce((total, row) => total + row.metrics[0], 0);
    const firstTotal = sum(report.rows);
    const secondTotal = sum(report.previousRows);
    if (!firstTotal && !secondTotal) return [];

    const before = new Map<string, number>();
    report.previousRows.forEach((row) =>
      before.set(row.dimensions[0], row.metrics[0])
    );

    const seen = new Set<string>();
    const rows: { name: string; first: number; second: number; change: number }[] = [];

    report.rows.forEach((row) => {
      const name = row.dimensions[0];
      seen.add(name);
      const first = firstTotal ? (row.metrics[0] / firstTotal) * 100 : 0;
      const second = secondTotal ? ((before.get(name) || 0) / secondTotal) * 100 : 0;
      rows.push({ name, first, second, change: first - second });
    });

    // Anything that disappeared entirely still moved, so keep it.
    report.previousRows.forEach((row) => {
      const name = row.dimensions[0];
      if (seen.has(name)) return;
      const second = secondTotal ? (row.metrics[0] / secondTotal) * 100 : 0;
      rows.push({ name, first: 0, second, change: -second });
    });

    // Pick the biggest movers in either direction, then show them in order.
    // Sorting before cutting would keep only risers and drop every fall.
    return rows
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
      .slice(0, 12)
      .sort((a, b) => b.change - a.change);
  }

  private total(rows: ReportRow[]): number {
    return rows.reduce((sum, row) => sum + row.metrics[0], 0);
  }

  public get engagement(): ReportRow | null {
    const rows = this.rows('engagement');
    return rows.length ? rows[0] : null;
  }

  public get emptySearchCount(): number {
    const zero = this.rows('emptySearches').find((row) => row.dimensions[0] === '0');
    return zero ? zero.metrics[0] : 0;
  }

  /**
   * Searches whose result count was actually recorded.
   *
   * Only some clients report it, so counting every search here would divide a
   * small measured slice by everything and overstate the failure rate.
   */
  public get searchTotal(): number {
    return this.rows('emptySearches')
      .filter((row) => row.dimensions[0] !== '(not set)')
      .reduce((sum, row) => sum + row.metrics[0], 0);
  }

  // ----- export ----------------------------------------------------------

  @HostListener('document:keydown.escape')
  public closeExport(): void {
    this.showExport = false;
  }

  public toggleExport(): void {
    this.showExport = !this.showExport;
  }

  public chooseAll(tab: 'overview' | 'compare', on: boolean): void {
    this.exportBlocks
      .filter((block) => block.tab === tab)
      .forEach((block) => (this.chosen[block.id] = on));
  }

  public get chosenCount(): number {
    return this.exportBlocks.filter((block) => this.chosen[block.id]).length;
  }

  /** Rows for one block, as plain text, ready to write into the document. */
  private exportTable(
    id: string
  ): { head: string[]; body: string[][]; notes?: string[] } | null {
    const pct = (value: number) => `${value.toFixed(1)}%`;
    const count = (value: number) => Math.round(value).toLocaleString();

    if (id === 'visitors') {
      if (!this.compareTotals.length) return null;
      const dates = this.data.comparison;
      return {
        head: [
          'Measure',
          this.periodLabel(dates.secondStart, dates.secondEnd),
          this.periodLabel(dates.firstStart, dates.firstEnd),
          'Change',
        ],
        body: this.compareTotals.map((row) => [
          row.measure,
          row.earlierText,
          row.laterText,
          row.change === null ? 'new' : `${Math.round(row.change)}%`,
        ]),
        // These measures are easy to misread, so each one says what it counts.
        notes: this.compareTotals.map((row) => row.explain),
      };
    }

    if (id === 'engagement') {
      const row = this.engagement;
      if (!row) return null;
      return {
        head: ['Measure', 'Value'],
        body: [
          ['Average visit', `${(row.metrics[0] / 60).toFixed(1)} minutes`],
          ['Visits over 10 seconds or two pages', pct(row.metrics[1] * 100)],
        ],
      };
    }

    if (id === 'emptySearches') {
      if (!this.searchTotal) return null;
      return {
        head: ['Measure', 'Value'],
        body: [
          ['Searches that found nothing', count(this.emptySearchCount)],
          ['Searches with a result count recorded', count(this.searchTotal)],
        ],
      };
    }

    if (id === 'cityCategories') {
      const body: string[][] = [];
      this.cityCategories.forEach((group) =>
        group.rows.forEach((row) =>
          body.push([group.city, row.dimensions[2], count(row.metrics[0])])
        )
      );
      return body.length ? { head: ['Place', 'Category', 'Times'], body } : null;
    }

    if (id === 'trafficQuality') {
      const body = this.places.map((row) => [
        `${row.dimensions[1]}, ${row.dimensions[0]}`,
        count(row.metrics[0]),
      ]);
      return body.length ? { head: ['Place', 'People'], body } : null;
    }

    if (id === 'languageEngagement') {
      const body = this.languageRows.map((row) => [
        row.name,
        row.people ? count(row.people) : '-',
        row.picked ? count(row.picked) : '-',
        row.people ? `${(row.seconds / 60).toFixed(1)} min` : '-',
      ]);
      return body.length ? {
        head: ['Language', 'Device set to', 'Picked in MyLink', 'Average visit'],
        body,
      } : null;
    }

    const section = this.compareSections.find((item) => item.key === id);
    if (section) {
      const rows = this.shares[id] || [];
      if (!rows.length || !this.data) return null;
      const dates = this.data.comparison;
      return {
        head: [
          section.title,
          `${dates.secondStart} to ${dates.secondEnd}`,
          `${dates.firstStart} to ${dates.firstEnd}`,
          'Change',
        ],
        body: rows.map((row) => [
          row.name,
          pct(row.second),
          pct(row.first),
          `${row.change > 0 ? '+' : ''}${row.change.toFixed(1)} pts`,
        ]),
      };
    }

    const rows = this.rows(id);
    if (!rows.length) return null;
    if (id === 'programEngagement') {
      return {
        head: ['Action', 'Times'],
        body: rows.map((row) => [this.eventLabel(row.dimensions[0]), count(row.metrics[0])]),
      };
    }
    if (id === 'topCategories') {
      return {
        head: ['Category', 'Times picked'],
        body: rows.map((row) => [row.dimensions[0], count(row.metrics[0])]),
      };
    }
    if (id === 'topSearches') {
      return {
        head: ['Search', 'Times'],
        body: rows.map((row) => [row.dimensions[0], count(row.metrics[0])]),
      };
    }
    return null;
  }

  /** Chart pictures for whichever tab is on screen. */
  private captureCharts(): { [id: string]: string } {
    const pictures: { [id: string]: string } = {};
    this.charts.forEach((chart: any) => {
      const id = (chart.canvas as HTMLCanvasElement).id.replace('compare-', '');
      try {
        // A tooltip left open by the pointer is painted into the picture.
        chart.setActiveElements([]);
        if (chart.tooltip) chart.tooltip.setActiveElements([], { x: 0, y: 0 });
        chart.update('none');
        pictures[id] = chart.toBase64Image();
      } catch (error) {
        // A chart with no size cannot be drawn. The block still gets its table.
      }
    });
    return pictures;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((done) => setTimeout(done, ms));
  }

  public async exportPdf(): Promise<void> {
    if (!this.data || this.exporting) return;
    this.exporting = true;
    this.showExport = false;

    const wanted = this.exportBlocks.filter((block) => this.chosen[block.id]);
    const started = this.tab;
    const pictures: { [id: string]: string } = {};

    try {
      // Charts only exist on the tab that is showing, so visit each tab that
      // has something selected and take its pictures before building the file.
      for (const tab of ['overview', 'compare'] as const) {
        if (!wanted.some((block) => block.tab === tab)) continue;
        if (this.tab !== tab) {
          this.setTab(tab);
          await this.wait(450);
        }
        Object.assign(pictures, this.captureCharts());
      }
      if (this.tab !== started) {
        this.setTab(started);
        await this.wait(50);
      }

      this.writePdf(wanted, pictures);
    } finally {
      this.exporting = false;
    }
  }

  private writePdf(
    blocks: { id: string; tab: string; title: string }[],
    pictures: { [id: string]: string }
  ): void {
    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = 595;
    const pageHeight = 842;
    const margin = 40;
    const width = pageWidth - margin * 2;
    let y = margin;

    const room = (needed: number) => {
      if (y + needed <= pageHeight - margin) return;
      doc.addPage();
      y = margin;
    };

    const rightOf = (text: string, size: number) => {
      const own = doc.getStringUnitWidth(text) * size / doc.internal.scaleFactor;
      return pageWidth - margin - own;
    };

    doc.setFontSize(18);
    doc.text('MyLink analytics', margin, y);
    const stamp = `Generated ${new Date().toLocaleString()}`;
    doc.setFontSize(9);
    doc.text(stamp, rightOf(stamp, 9), y);
    y += 18;

    // The header carries the range the overview blocks used. The two compared
    // periods belong with the compared blocks, further down, because they say
    // nothing about the overview blocks above them.
    doc.setFontSize(10);
    if (blocks.some((block) => block.tab === 'overview')) {
      doc.text(this.rangeDates, margin, y);
      y += 14;
    }
    y += 22;

    let comparePrinted = false;

    blocks.forEach((block) => {
      const table = this.exportTable(block.id);
      const picture = pictures[block.id];
      if (!table && !picture) return;

      // Once, in front of the first compared block that has something to show.
      if (block.tab === 'compare' && !comparePrinted && this.data) {
        const dates = this.data.comparison;
        const earlier = this.periodLabel(dates.secondStart, dates.secondEnd);
        const later = this.periodLabel(dates.firstStart, dates.firstEnd);
        room(48);
        doc.setFontSize(10);
        doc.text(`Compared: ${earlier} with ${later}`, margin, y);
        y += 22;
        comparePrinted = true;
      }

      room(60);
      doc.setFontSize(13);
      doc.text(block.title, margin, y);
      y += 16;

      if (picture) {
        const height = 150;
        room(height + 10);
        doc.addImage(picture, 'PNG', margin, y, width, height);
        y += height + 12;
      }

      if (table) {
        const columns = table.head.length;
        const first = margin;
        const step = (width - 180) / Math.max(columns - 1, 1);
        const at = (index: number) =>
          index === 0 ? first : margin + 180 + step * (index - 1);

        doc.setFontSize(9);
        doc.setFontStyle('bold');
        // A period name is wider than its column, so a header wraps rather
        // than running into the one beside it.
        const room_for = (index: number) => (index === 0 ? 170 : step - 10);
        const wrapped = table.head.map((cell, index) =>
          doc.splitTextToSize(cell, room_for(index)) as string[]
        );
        const lines = wrapped.reduce((most, cell) => Math.max(most, cell.length), 1);
        room(12 * lines + 8);
        wrapped.forEach((cell, index) => doc.text(cell, at(index), y));
        doc.setFontStyle('normal');
        y += 12 * lines;

        table.body.forEach((row, rowIndex) => {
          const note = table.notes ? table.notes[rowIndex] : '';
          const wrappedNote = note ?
            doc.splitTextToSize(note, 170) as string[] :
            [];

          // A category or search term is often wider than its column.
          const wrappedName = doc.splitTextToSize(row[0], 170) as string[];
          const nameLines = Math.max(wrappedName.length, 1);

          room(12 * nameLines + wrappedNote.length * 9 + 6);
          row.forEach((cell, index) => {
            doc.text(index === 0 ? wrappedName : cell, at(index), y);
          });
          y += 12 * nameLines;

          if (wrappedNote.length) {
            doc.setFontSize(7.5);
            doc.setTextColor(110);
            doc.text(wrappedNote, margin, y);
            doc.setTextColor(0);
            doc.setFontSize(9);
            y += wrappedNote.length * 9 + 4;
          }
        });
      }

      y += 18;
    });

    doc.save(`mylnk-analytics-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  private destroyCharts(): void {
    this.charts.forEach((chart) => chart.destroy());
    this.charts = [];
  }

  private drawCharts(): void {
    if (!this.viewReady || !this.data) return;
    this.destroyCharts();

    this.specs.forEach((spec) => {
      const canvas = this.canvases.find(
        (item) => item.nativeElement.id === spec.canvasId
      );
      const rows = this.rows(spec.key);
      if (!canvas || rows.length === 0) return;

      const report = this.data.reports[spec.key];
      const bucket = report.dimensionNames[0];
      const labels = rows.map((row) =>
        spec.type === 'line' ?
          this.timeLabel(row.dimensions[0], bucket) :
          row.dimensions.map((value) => this.eventLabel(value)).join(' - ')
      );

      const config: ChartConfiguration = {
        type: spec.type,
        data: {
          labels,
          datasets: spec.metricIndexes.map((index) => ({
            label: this.metricLabel(report.metricNames[index]),
            data: this.hideLeadingGap(rows.map((row) => row.metrics[index] || 0)),
            spanGaps: false,
            borderWidth: spec.type === 'line' ? 2 : 0,
            fill: false,
            tension: 0.2,
            pointRadius: rows.length > 60 ? 0 : 2,
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: spec.horizontal ? 'y' : 'x',
          // A horizontal bar chart has to match on the vertical axis, or
          // hovering a row picks whichever bar is nearest left to right.
          interaction: {
            mode: 'index',
            intersect: false,
            axis: spec.horizontal ? 'y' : 'x',
          },
          plugins: { legend: { display: spec.metricIndexes.length > 1 } },
          // On a horizontal bar chart the names sit on y and the counts on x.
          // Chart.js drops every other name when they do not fit, which left
          // bars standing against the wrong label. autoSkip off names them all.
          scales: spec.horizontal ?
            {
              x: { beginAtZero: true },
              y: { grid: { display: false }, ticks: { autoSkip: false } },
            } :
            { x: { grid: { display: false } }, y: { beginAtZero: true } },
        },
      };

      this.charts.push(new Chart(canvas.nativeElement, config));
    });

    this.drawCompareCharts();
  }

  /**
   * One line per item, over the whole span the two periods cover.
   *
   * Counts rather than share here, so the lines can be read against each
   * other. The table below each chart carries the share.
   */
  private drawCompareCharts(): void {
    if (!this.compareCanvases || !this.data) return;

    this.drawVisitorsChart();

    this.compareSections.forEach((section) => {
      const names = this.compareTop[section.key] || [];
      const report = this.data.reports[section.trend];
      if (!names.length || !report) return;

      const canvas = this.compareCanvases.find(
        (item) => item.nativeElement.id === 'compare-' + section.key
      );
      if (!canvas) return;

      const bucket = report.dimensionNames[0] || 'month';
      const later = this.collectSeries(report.rows || [], names);
      const earlier = this.collectSeries(report.previousRows || [], names);
      const length = Math.max(later.times.length, earlier.times.length);
      if (length < 2) return;

      const labels = this.positionLabels(later.times, earlier.times, bucket);
      const datasets: any[] = [];

      // Each item gets a solid line for the later period and a dashed line of
      // the same colour for the earlier one. Six items at once is unreadable,
      // so only the biggest movers start on and the legend toggles the rest.
      if (!this.compareHidden[section.key]) this.compareHidden[section.key] = {};
      const choices = this.compareHidden[section.key];

      names.forEach((name, index) => {
        const colour = this.lineColours[index % this.lineColours.length];
        if (choices[name] === undefined) choices[name] = index >= VISIBLE_AT_FIRST;
        const hidden = choices[name];

        // The earlier period comes first everywhere on this tab, so the
        // legend and the tooltip read in the same order as the tables.
        datasets.push({
          label: name + EARLIER_SUFFIX,
          data: this.alignSeries(earlier, name, length),
          borderColor: colour,
          backgroundColor: colour,
          borderWidth: 1,
          borderDash: [4, 4],
          fill: false,
          tension: 0.25,
          pointRadius: 0,
          hidden,
        });
        datasets.push({
          label: name,
          data: this.alignSeries(later, name, length),
          borderColor: colour,
          backgroundColor: colour,
          borderWidth: 2,
          fill: false,
          tension: 0.25,
          pointRadius: length > 40 ? 0 : 2,
          hidden,
        });
      });

      const config: ChartConfiguration = {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false, axis: 'x' },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              // The dashed line repeats a name that is already in the legend.
              labels: { filter: (item: any) => !this.isEarlier(item.text) },
              // One click shows or hides both periods of the item.
              onClick: (event: any, item: any, legend: any) =>
                this.toggleItem(legend.chart, section.key, item.text),
            },
          },
          scales: {
            x: { grid: { display: false } },
            y: { beginAtZero: true },
          },
        },
      };

      this.charts.push(new Chart(canvas.nativeElement, config));
    });
  }

  /**
   * Says whether the reader has turned an item off.
   *
   * @param {string} key The comparison the item belongs to.
   * @param {string} name The item.
   * @return {boolean} True when the item is hidden.
   */
  public isItemHidden(key: string, name: string): boolean {
    const choices = this.compareHidden[key];
    return !!choices && choices[name] === true;
  }

  /**
   * Shows or hides an item from the buttons beside the chart.
   *
   * The legend sits inside the canvas, which the keyboard cannot reach, so
   * these buttons are the only route for a reader who does not use a mouse.
   *
   * @param {string} key The comparison the item belongs to.
   * @param {string} name The item to show or hide.
   * @return {void}
   */
  public toggleItemByName(key: string, name: string): void {
    const chart = this.charts.find(
      (item) => (item.canvas as HTMLCanvasElement).id === 'compare-' + key
    );
    if (!chart) return;
    this.toggleItem(chart, key, name);
  }

  /**
   * Shows or hides both lines of one item on a comparison chart.
   *
   * @param {Chart} chart The chart the legend belongs to.
   * @param {string} key The comparison the chart belongs to.
   * @param {string} name The item the reader clicked.
   * @return {void}
   */
  private toggleItem(chart: any, key: string, name: string): void {
    const datasets = chart.data.datasets as { label: string }[];
    let solid = -1;
    datasets.forEach((dataset, index) => {
      if (dataset.label === name) solid = index;
    });
    if (solid < 0) return;

    const show = !chart.isDatasetVisible(solid);
    datasets.forEach((dataset, index) => {
      if (dataset.label === name || dataset.label === name + EARLIER_SUFFIX) {
        chart.setDatasetVisibility(index, show);
      }
    });

    // Kept so a rebuild, such as the one the export causes, draws the same
    // lines the reader chose.
    if (!this.compareHidden[key]) this.compareHidden[key] = {};
    this.compareHidden[key][name] = !show;
    chart.update();
  }

  /** True for a line that carries the earlier of the two periods. */
  private isEarlier(label: string): boolean {
    if (!label) return false;
    // indexOf returns -1 when the suffix is absent, which also equals
    // label.length - EARLIER_SUFFIX.length for a label of exactly 16
    // characters. A category named "Housing Services" hit that.
    const at = label.length - EARLIER_SUFFIX.length;
    return at >= 0 && label.indexOf(EARLIER_SUFFIX, at) === at;
  }

  /**
   * Splits a list into two columns, so a long table does not run the card
   * off the bottom of the screen. A short list stays in one column, because
   * two columns of four rows read worse than one column of eight.
   *
   * The first column takes the extra row when the count is odd, which keeps
   * the order top to bottom then left to right.
   */
  private splitInTwo(rows: ReportRow[]): ReportRow[][] {
    if (rows.length < SPLIT_ABOVE) return rows.length ? [rows] : [];
    const half = Math.ceil(rows.length / 2);
    return [rows.slice(0, half), rows.slice(half)];
  }

  /** Groups rows by name, then by time, for one period. */
  private collectSeries(rows: ReportRow[], names: string[]) {
    const times: string[] = [];
    const series = new Map<string, Map<string, number>>();
    rows.forEach((row) => {
      const time = row.dimensions[0];
      const name = row.dimensions[1];
      if (!time) return;
      // Every period the report covers becomes a position on the axis, even
      // when none of the chosen names appear in it. The chart pairs the two
      // periods by position, so collecting only the periods a chosen name
      // appears in leaves the two sides different lengths. The axis then
      // ran out of later periods, fell back to earlier ones, and drew each
      // solid line against a month it does not belong to.
      if (times.indexOf(time) === -1) times.push(time);
      if (!name || names.indexOf(name) === -1) return;
      if (!series.has(name)) series.set(name, new Map());
      series.get(name).set(time, row.metrics[0]);
    });
    times.sort();
    return { times, series };
  }

  /** Puts one name's values on the shared axis, a null past the period end. */
  private alignSeries(
    part: { times: string[]; series: Map<string, Map<string, number>> },
    name: string,
    length: number
  ): (number | null)[] {
    const values: (number | null)[] = [];
    for (let index = 0; index < length; index++) {
      const time = part.times[index];
      if (time === undefined) {
        values.push(null);
        continue;
      }
      const found = part.series.get(name);
      values.push(found ? found.get(time) || 0 : 0);
    }
    return values;
  }

  /** Names the points on the shared axis, from whichever period is longer. */
  private positionLabels(later: string[], earlier: string[], bucket: string): string[] {
    const length = Math.max(later.length, earlier.length);
    const labels: string[] = [];
    for (let index = 0; index < length; index++) {
      const time = later[index] || earlier[index];
      labels.push(time ? this.timeLabel(time, bucket) : '');
    }
    return labels;
  }

  /** Reads one period as "1 Sep - 14 Sep 2025". */
  public periodLabel(start: string, end: string): string {
    if (!start || !end) return '';
    const day = (value: string) => this.timeLabel(value.split('-').join(''), 'date');
    const startYear = start.substring(0, 4);
    const endYear = end.substring(0, 4);
    return startYear === endYear ?
      `${day(start)} to ${day(end)} ${endYear}` :
      `${day(start)} ${startYear} to ${day(end)} ${endYear}`;
  }

  /** Event names read badly on a chart axis. */
  public eventLabel(name: string): string {
    const words: { [key: string]: string } = {
      program_viewed: 'Opened a program page',
      program_phone_tapped: 'Called',
      program_directions_tapped: 'Got directions',
      program_website_tapped: 'Visited the website',
    };
    return words[name] || name;
  }

  /**
   * GA hands back 20190104 or 201901. Neither belongs on an axis.
   *
   * @param {string} value The raw GA date or month.
   * @param {string} bucket Either "date" or "month".
   * @return {string} A label a person can read.
   */
  public timeLabel(value: string, bucket: string): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (bucket === 'month' && value.length === 6) {
      return `${months[Number(value.substring(4, 6)) - 1]} ${value.substring(0, 4)}`;
    }
    if (value.length === 8) {
      const month = months[Number(value.substring(4, 6)) - 1];
      return `${Number(value.substring(6, 8))} ${month}`;
    }
    return value;
  }

  /**
   * Turns the leading run of zeroes into gaps.
   *
   * GA4 did not record sessions for the app in the Firebase years, so an
   * engaged figure is zero until late 2021. Drawn as zero it looks like MyLink
   * suddenly took off. It was a change in what was measured.
   *
   * @param {number[]} values One series.
   * @return {(number | null)[]} The same series, with the opening run blank.
   */
  private hideLeadingGap(values: number[]): (number | null)[] {
    const firstReal = values.findIndex((value) => value > 0);
    if (firstReal <= 0) return values;
    return values.map((value, index) => (index < firstReal ? null : value));
  }

  /** People per month across both compared periods. */
  private drawVisitorsChart(): void {
    const report = this.data.reports['visitorsTrend'];
    const canvas = this.compareCanvases.find(
      (item) => item.nativeElement.id === 'compare-visitors'
    );
    if (!canvas || !report || report.rows.length < 2) return;

    const bucket = report.dimensionNames[0] || 'month';
    const read = (rows: ReportRow[]) => {
      const values = new Map<string, number>();
      rows.forEach((row) => values.set(row.dimensions[0], row.metrics[0]));
      return Array.from(values.keys()).sort().map((time) => values.get(time));
    };

    // Both periods sit on the same day-of-period axis, so the reader compares
    // the two shapes directly however many years separate them.
    const laterTimes = (report.rows || []).map((row) => row.dimensions[0]).sort();
    const earlierTimes = (report.previousRows || []).map((row) => row.dimensions[0]).sort();
    const later = read(report.rows || []);
    const earlier = read(report.previousRows || []);
    const length = Math.max(later.length, earlier.length);
    if (length < 2) return;

    const dates = this.data.comparison;
    const pad = (values: number[]) => {
      const padded: (number | null)[] = [];
      for (let index = 0; index < length; index++) {
        padded.push(index < values.length ? values[index] : null);
      }
      return padded;
    };

    this.charts.push(
      new Chart(canvas.nativeElement, {
        type: 'line',
        data: {
          labels: this.positionLabels(laterTimes, earlierTimes, bucket),
          datasets: [
            {
              label: this.periodLabel(dates.secondStart, dates.secondEnd),
              data: pad(earlier),
              borderColor: '#9e9e9e',
              backgroundColor: '#9e9e9e',
              borderWidth: 1,
              borderDash: [4, 4],
              fill: false,
              tension: 0.25,
              pointRadius: 0,
            },
            {
              label: this.periodLabel(dates.firstStart, dates.firstEnd),
              data: pad(later),
              borderColor: '#3f51b5',
              backgroundColor: '#3f51b5',
              borderWidth: 2,
              fill: false,
              tension: 0.25,
              pointRadius: length > 40 ? 0 : 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false, axis: 'x' },
          plugins: { legend: { display: true, position: 'top' } },
          scales: { x: { grid: { display: false } }, y: { beginAtZero: true } },
        },
      })
    );
  }

  /** GA metric names read badly on a chart legend. */
  public metricLabel(name: string): string {
    const words: { [key: string]: string } = {
      engagedSessions: 'Engaged visits',
      activeUsers: 'People',
      eventCount: 'Times',
      averageSessionDuration: 'Average visit',
      sessions: 'Visits',
    };
    return words[name] || name;
  }
}
